/**
 * @fileoverview Componente responsável por criar e editar publicações de novidades.
 */

import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import { Paperclip, Pencil, Plus, Trash2 } from "lucide-react";
import { head } from "lodash";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  FormControl,
  Grid,
  MenuItem,
  Select,
  Chip,
  Typography,
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '24px',
      background: theme.palette.type === 'dark'
        ? 'linear-gradient(135deg, rgba(18, 18, 19, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)'
        : '#FFFFFF',
      boxShadow: theme.palette.type === 'dark'
        ? '0 24px 80px rgba(0, 0, 0, 0.6)'
        : '0 24px 80px rgba(0, 0, 0, 0.12)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : 'none',
    },
    '& .MuiBackdrop-root': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)',
    },
  },
  dialogTitle: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.15)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    '& .MuiTypography-root': {
      fontSize: '1.5rem',
      fontWeight: 500,
      color: '#ffffff',
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(61, 92, 253, 0.04)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.25)' : '1px solid rgba(61, 92, 253, 0.15)',
      },
      '&.Mui-focused': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 1)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.4)' : '1px solid rgba(61, 92, 253, 0.3)',
        boxShadow: '0 0 0 1px rgba(91, 124, 255, 0.15)',
      },
    },
  },
  btnWrapper: {
    position: "relative",
  },
  actionBtn: {
    height: 36,
    minWidth: 0,
    padding: "0 14px",
    borderRadius: 10,
    textTransform: "none",
    fontSize: "0.875rem",
    fontWeight: 500,
    boxShadow: "none",
    "& .MuiButton-startIcon": {
      marginRight: 6,
    },
    "&:hover": {
      boxShadow: "none",
    },
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  selectContainer: {
    width: '100%',
  },
  select: {
    backgroundColor: theme.palette.type === 'light' ? '#f8fafc' : 'rgba(39, 39, 42, 0.8)',
    borderRadius: '14px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fontWeight: 500,
    cursor: 'pointer',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.type === 'light' ? '#e2e8f0' : 'rgba(91, 124, 255, 0.3)',
      borderWidth: '1.5px',
      transition: 'all 0.3s ease',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.type === 'light' ? theme.palette.primary.main : 'rgba(91, 124, 255, 0.7)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
      boxShadow: theme.palette.type === 'dark'
        ? '0 0 0 1px rgba(91, 124, 255, 0.15)'
        : '0 0 0 1px rgba(61, 92, 253, 0.1)',
    },
    '& .MuiSelect-select': {
      color: theme.palette.type === 'light' ? '#1e293b' : '#FFFFFF',
      padding: '14px 16px',
      cursor: 'pointer',
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.type === 'light' ? '#64748b' : '#5B7CFF',
    },
  },
  menuPaper: {
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(24, 24, 27, 0.98) !important' : '#ffffff',
    borderRadius: '12px',
    border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.3)' : '1px solid #e2e8f0',
    marginTop: '4px',
    boxShadow: theme.palette.type === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.6)'
      : '0 4px 12px rgba(0, 0, 0, 0.1)',
    '& .MuiList-root': {
      padding: '8px !important',
    },
    '& .MuiMenuItem-root': {
      color: theme.palette.type === 'dark' ? '#F5F5F5' : '#1e293b',
      fontSize: '0.875rem',
      padding: '10px 14px !important',
      borderRadius: '8px',
      margin: '2px 4px !important',
      minHeight: 'auto !important',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(91, 124, 255, 0.15)' : 'rgba(61, 92, 253, 0.08)',
      },
      '&.Mui-selected': {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(91, 124, 255, 0.25)' : 'rgba(61, 92, 253, 0.12)',
        '&:hover': {
          backgroundColor: theme.palette.type === 'dark' ? 'rgba(91, 124, 255, 0.35)' : 'rgba(61, 92, 253, 0.16)',
        },
      },
    },
  },
  companySectionTitle: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.85)' : '#1e293b',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  companySectionHelp: {
    fontSize: '0.75rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.55)' : '#64748b',
    marginBottom: theme.spacing(1.5),
    display: 'block',
  },
  companyChipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  companyChip: {
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, color 0.15s ease',
  },
  companyChipActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  companyChipActionButton: {
    fontSize: '0.75rem',
    padding: '4px 12px',
    borderRadius: '8px',
    textTransform: 'none',
  },
  companyChipError: {
    fontSize: '0.75rem',
    color: '#ef4444',
    marginTop: theme.spacing(0.5),
  },
  quillEditor: {
    '& .ql-toolbar': {
      borderRadius: '14px 14px 0 0',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
      '& .ql-stroke': {
        stroke: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#444',
      },
      '& .ql-fill': {
        fill: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#444',
      },
      '& .ql-picker-label': {
        color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#444',
      },
    },
    '& .ql-container': {
      borderRadius: '0 0 14px 14px',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
      borderTop: 'none',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      minHeight: 180,
      fontSize: '0.95rem',
    },
    '& .ql-editor': {
      minHeight: 180,
      color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#1e293b',
      '&.ql-blank::before': {
        color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8',
        fontStyle: 'italic',
      },
    },
    '& .ql-picker-options': {
      background: theme.palette.type === 'dark' ? '#1a1a1e' : '#fff',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : '1px solid rgba(0,0,0,0.1)',
    },
  },
}));

const NewsPostSchema = Yup.object().shape({
  title: Yup.string().required("Título é obrigatório"),
  companiesIds: Yup.array()
    .of(Yup.number())
    .min(1, "Selecione pelo menos uma empresa"),
});

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ color: [] }, { background: [] }],
    ['link', 'image'],
    ['clean'],
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'color', 'background',
  'link', 'image',
];

const NewsPostModal = ({ open, onClose, newsPostId, reload }) => {
  const classes = useStyles();

  const initialState = {
    title: "",
    author: "",
    text: "",
    status: true,
    companiesIds: [],
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [newsPost, setNewsPost] = useState(initialState);
  const [attachment, setAttachment] = useState(null);
  const [companies, setCompanies] = useState([]);
  const attachmentFile = useRef(null);

  // Carrega lista de empresas pra popular o multi-select (apenas super-user
  // acessa essa rota — mesmo guard que protege /news-posts no backend).
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/companies/list");
        setCompanies(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open]);

  useEffect(() => {
    try {
      (async () => {
        if (!newsPostId) return;

        const { data } = await api.get(`/news-posts/${newsPostId}`);
        // O backend retorna `companies` (objetos {id, name}); o formulário
        // trabalha com `companiesIds` (array de ids). Mapear aqui.
        const companiesIds = Array.isArray(data.companies)
          ? data.companies.map((c) => c.id)
          : [];
        setNewsPost((prevState) => {
          return { ...prevState, ...data, companiesIds };
        });
      })();
    } catch (err) {
      toastError(err);
    }
  }, [newsPostId, open]);

  const handleClose = () => {
    setNewsPost(initialState);
    setAttachment(null);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSaveNewsPost = async (values) => {
    const newsPostData = { ...values };
    try {
      if (newsPostId) {
        await api.put(`/news-posts/${newsPostId}`, newsPostData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/news-posts/${newsPostId}/media-upload`, formData);
        }
      } else {
        const { data } = await api.post("/news-posts", newsPostData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/news-posts/${data.id}/media-upload`, formData);
        }
      }
      toast.success("Novidade salva com sucesso!");
      if (typeof reload == "function") {
        reload();
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (newsPost.mediaPath) {
      await api.delete(`/news-posts/${newsPost.id}/media-upload`);
      setNewsPost((prev) => ({
        ...prev,
        mediaPath: null,
      }));
      toast.success("Arquivo excluído!");
      if (typeof reload == "function") {
        reload();
      }
    }
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title="Excluir anexo"
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        Deseja realmente excluir este anexo?
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        className={classes.dialog}
      >
        <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
          {newsPostId ? "Editar Novidade" : "Nova Novidade"}
        </DialogTitle>
        <div style={{ display: "none" }}>
          <input
            type="file"
            accept=".png,.jpg,.jpeg,.gif,.webp"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={newsPost}
          enableReinitialize={true}
          validationSchema={NewsPostSchema}
          validateOnChange={false}
          validateOnBlur={true}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveNewsPost(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Grid spacing={2} container>
                  <Grid xs={12} item>
                    <Field
                      as={TextField}
                      label="Título"
                      name="title"
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <Field
                      as={TextField}
                      label="Autor"
                      name="author"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.textField}
                      placeholder="Ex: Equipe, Admin, Nome..."
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <div className={classes.quillEditor}>
                      <ReactQuill
                        theme="snow"
                        value={values.text}
                        onChange={(content) => setFieldValue("text", content)}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Escreva o conteúdo da novidade..."
                      />
                    </div>
                  </Grid>
                  <Grid xs={12} item>
                    <FormControl className={classes.selectContainer} variant="outlined" size="small">
                      <Select
                        className={classes.select}
                        value={values.status}
                        onChange={(e) => setFieldValue("status", e.target.value)}
                        MenuProps={{
                          classes: { paper: classes.menuPaper },
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          getContentAnchorEl: null,
                          style: { zIndex: 10000 },
                        }}
                      >
                        <MenuItem value={true}>Ativo</MenuItem>
                        <MenuItem value={false}>Inativo</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid xs={12} item>
                    <Typography className={classes.companySectionTitle}>
                      Empresas que verão esta novidade
                    </Typography>
                    <Typography component="span" className={classes.companySectionHelp}>
                      Selecione uma ou mais empresas. A novidade só aparecerá no painel dos usuários das empresas marcadas.
                    </Typography>
                    <div className={classes.companyChipActions}>
                      <Button
                        size="small"
                        variant="outlined"
                        className={classes.companyChipActionButton}
                        onClick={() =>
                          setFieldValue(
                            "companiesIds",
                            companies.map((c) => c.id)
                          )
                        }
                        disabled={companies.length === 0}
                      >
                        Selecionar todas
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        className={classes.companyChipActionButton}
                        onClick={() => setFieldValue("companiesIds", [])}
                        disabled={
                          !values.companiesIds || values.companiesIds.length === 0
                        }
                      >
                        Limpar
                      </Button>
                    </div>
                    <div className={classes.companyChipsContainer}>
                      {companies.map((company) => {
                        const selected = (values.companiesIds || []).includes(company.id);
                        return (
                          <Chip
                            key={company.id}
                            label={company.name}
                            size="small"
                            className={classes.companyChip}
                            color={selected ? "primary" : "default"}
                            onClick={() => {
                              const current = values.companiesIds || [];
                              const next = selected
                                ? current.filter((id) => id !== company.id)
                                : [...current, company.id];
                              setFieldValue("companiesIds", next);
                            }}
                          />
                        );
                      })}
                    </div>
                    {touched.companiesIds && errors.companiesIds && (
                      <Typography component="span" className={classes.companyChipError}>
                        {errors.companiesIds}
                      </Typography>
                    )}
                  </Grid>

                  {(newsPost.mediaPath || attachment) && (
                    <Grid xs={12} item>
                      <Button startIcon={<Paperclip />}>
                        {attachment ? attachment.name : newsPost.mediaName}
                      </Button>
                      <IconButton
                        onClick={() => setConfirmationOpen(true)}
                        color="secondary"
                      >
                        <Trash2 />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                {!attachment && !newsPost.mediaPath && (
                  <Button
                    color="primary"
                    onClick={() => attachmentFile.current.click()}
                    disabled={isSubmitting}
                    variant="outlined"
                    className={classes.actionBtn}
                    startIcon={<Paperclip size={15} strokeWidth={1.75} />}
                  >
                    Anexar Imagem
                  </Button>
                )}
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                  className={classes.actionBtn}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={`${classes.actionBtn} ${classes.btnWrapper}`}
                  startIcon={
                    newsPostId ? (
                      <Pencil size={15} strokeWidth={1.75} />
                    ) : (
                      <Plus size={15} strokeWidth={1.75} />
                    )
                  }
                >
                  {newsPostId ? "Salvar" : "Criar"}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default NewsPostModal;
